import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HiDownload } from 'react-icons/hi';
import { Modal, Button, TabContainer } from '@/components/common';
import { Supplier } from '@/types/supplier';
import { WarehouseReceipt, WarehouseReceiptLine } from '@/types/warehouseReceipt';
import { supplierApi } from '@/api/endpoints/suppliers';
import { warehouseReceiptApi, warehouseReceiptLineApi } from '@/api/endpoints/warehouseReceipts';
import { paymentVoucherApi } from '@/api/endpoints/paymentVouchers';
import { WarehouseReceiptExcelExport } from '@/components/warehouseReceipts/WarehouseReceiptExcelExport';
import { SupplierDebtExcelExport } from '@/utils/supplierDebtExcelExport';
import { toast } from '@/store/toastStore';
import SupplierInfoTab from './SupplierInfoTab';
import SupplierDebtTab from './SupplierDebtTab';
import SupplierDebtExportModal from './SupplierDebtExportModal';

interface SupplierDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplierId: string | null; // Actually supplier code, not ID
}

const SupplierDetailModal: React.FC<SupplierDetailModalProps> = ({
  isOpen,
  onClose,
  supplierId,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [warehouseReceipts, setWarehouseReceipts] = useState<WarehouseReceipt[]>([]);
  const [warehouseReceiptsLoading, setWarehouseReceiptsLoading] = useState(false);
  const [warehouseReceiptsLoaded, setWarehouseReceiptsLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('debt');

  // Debt export modal state
  const [showDebtExportModal, setShowDebtExportModal] = useState(false);

  // Track date range from debt tab
  const [debtDateFrom, setDebtDateFrom] = useState('');
  const [debtDateTo, setDebtDateTo] = useState('');

  // Fetch supplier data when modal opens and reset state when modal closes
  useEffect(() => {
    if (isOpen && supplierId) {
      fetchSupplierData();
    } else if (!isOpen) {
      // Reset state when modal closes
      setSupplier(null);
      setWarehouseReceipts([]);
      setWarehouseReceiptsLoaded(false);
      setActiveTab('debt');
    }
  }, [isOpen, supplierId]);

  // Auto-fetch warehouse receipts when supplier is loaded and debt tab is active (default tab)
  useEffect(() => {
    if (supplier && activeTab === 'debt' && !warehouseReceiptsLoaded) {
      fetchWarehouseReceipts();
    }
  }, [supplier, activeTab, warehouseReceiptsLoaded]);

  const fetchSupplierData = async () => {
    if (!supplierId) return;

    setLoading(true);
    try {
      // Fetch supplier basic info using supplier code
      const supplierData = await supplierApi.getSupplier(supplierId);
      setSupplier(supplierData);

    } catch (error: any) {
      console.error('Error fetching supplier data:', error);
      toast.error(t('common.error'), t('suppliers.messages.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch warehouse receipts only when needed (lazy loading)
  const fetchWarehouseReceipts = async () => {
    if (!supplier || warehouseReceiptsLoaded) return;

    setWarehouseReceiptsLoading(true);
    try {
      const receiptsResponse = await warehouseReceiptApi.getAll({
        supplier_id: supplier.code,
        page_size: 10, // Use pagination instead of loading all at once
        sort_by: 'created_at',
        sort_order: 'desc',
      });
      setWarehouseReceipts(receiptsResponse.data);
      setWarehouseReceiptsLoaded(true);

    } catch (error: any) {
      console.error('Error fetching warehouse receipts:', error);
      toast.error(t('common.error'), t('warehouseReceipts.messages.fetchError'));
    } finally {
      setWarehouseReceiptsLoading(false);
    }
  };

  // Handle tab change and lazy load data when needed
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);

    // Load warehouse receipts when history tab or debt tab is clicked
    if ((tabId === 'history' || tabId === 'debt') && !warehouseReceiptsLoaded) {
      fetchWarehouseReceipts();
    }
  };

  // Excel export functionality for warehouse receipts and debt
  const handleExcelExport = async () => {
    if (!supplier || warehouseReceipts.length === 0) {
      toast.error(t('common.error'), t('warehouseReceipts.messages.noData'));
      return;
    }

    // For debt tab, show date range selection modal
    if (activeTab === 'debt') {
      setShowDebtExportModal(true);
      return;
    }

    try {
      const excelExport = new WarehouseReceiptExcelExport({
        t,
        getStatusLabel: (status: string) => t(`warehouseReceipts.status.${status}`),
      });

      // Create a mock receipt with supplier info for export
      const mockReceipt: WarehouseReceipt = {
        id: 'export',
        code: supplier.code,
        supplier_id: supplier.id,
        supplier: {
          id: supplier.id,
          code: supplier.code,
          name: supplier.name,
          contactPerson: supplier.contactPerson,
          phone: supplier.phone,
          email: supplier.email,
          address: supplier.address,
          status: supplier.status,
          totalDebt: supplier.totalDebt || 0,
          createdAt: supplier.createdAt,
          updatedAt: supplier.updatedAt,
        },
        status: 'purchase_goods',
        note: '',
        total_amount: 0,
        paid_amount: 0,
        payment_status: {
          status: 'outstanding',
          note: 'Outstanding payment',
          created_at: new Date().toISOString(),
          created_by: '',
        },
        created_by: '',
        created_by_name: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      await excelExport.exportSupplierReceipts(mockReceipt);
      toast.success(t('common.success'), t('warehouseReceipts.messages.exportSuccess'));
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error(t('common.error'), t('warehouseReceipts.messages.exportError'));
    }
  };

  // Debt export functionality
  const handleDebtExport = async (dateFrom: string, dateTo: string) => {
    if (!supplier) {
      toast.error(t('common.error'), t('suppliers.messages.noSupplierSelected'));
      return;
    }

    try {
      // Filter warehouse receipts by date range
      const filteredReceipts = warehouseReceipts.filter(receipt => {
        const receiptDate = new Date(receipt.receipt_date || receipt.created_at);
        const fromDate = new Date(dateFrom);
        const toDate = new Date(dateTo);
        return receiptDate >= fromDate && receiptDate <= toDate && receipt.status === 'received';
      });

      // Fetch warehouse receipt lines for the filtered receipts in parallel
      const receiptLines: WarehouseReceiptLine[] = [];
      const linePromises = filteredReceipts.map(async (receipt) => {
        try {
          const lines = await warehouseReceiptLineApi.getAll({ warehouse_receipt_id: receipt.code });

          // Map the lines to include the correct warehouse_receipt_id for filtering
          return lines.data.map(line => ({
            ...line,
            warehouse_receipt_id: receipt.code // Use receipt.code for consistency
          }));
        } catch (error) {
          console.warn(`Failed to fetch lines for receipt ${receipt.code}:`, error);
          return [];
        }
      });

      const allLines = await Promise.all(linePromises);
      receiptLines.push(...allLines.flat());

      // Fetch payment vouchers for this supplier within date range
      const paymentVouchersResponse = await paymentVoucherApi.getAllPaymentVouchers({
        page: 1,
        page_size: 1000,
        type: 'supplier' as any,
        receiver_id: supplier.code,
      } as any);

      // Filter payment vouchers by date range
      const filteredPaymentVouchers = paymentVouchersResponse.data.filter(voucher => {
        const voucherDate = new Date(voucher.payment_date);
        const fromDate = new Date(dateFrom);
        const toDate = new Date(dateTo);
        return voucherDate >= fromDate && voucherDate <= toDate;
      });

      // Fetch debt adjustment vouchers for this supplier within date range
      const { debtAdjustmentVoucherApi } = await import('@/api/endpoints/debtAdjustmentVouchers');
      const debtAdjustmentsResponse = await debtAdjustmentVoucherApi.getAllDebtAdjustmentVouchers({
        page: 1,
        page_size: 1000,
        entity_type: 'supplier',
        entity_id: supplier.code,
        date_from: new Date(dateFrom).toISOString(),
        date_to: new Date(dateTo + 'T23:59:59.999Z').toISOString(),
      });

      // Filter debt adjustments by date range (already filtered by API, but double-check)
      const filteredDebtAdjustments = debtAdjustmentsResponse.data.filter(adjustment => {
        const adjustmentDate = new Date(adjustment.createdAt);
        const fromDate = new Date(dateFrom);
        const toDate = new Date(dateTo + 'T23:59:59.999Z');
        return adjustmentDate >= fromDate && adjustmentDate <= toDate;
      });

      // Validate data before export
      if (filteredReceipts.length === 0 && filteredPaymentVouchers.length === 0 && filteredDebtAdjustments.length === 0) {
        toast.error(t('common.error'), t('suppliers.debtExport.noDataInRange'));
        return;
      }

      // Generate Excel export
      const debtExport = new SupplierDebtExcelExport({
        supplier,
        warehouseReceipts: filteredReceipts,
        warehouseReceiptLines: receiptLines,
        paymentVouchers: filteredPaymentVouchers,
        debtAdjustmentVouchers: filteredDebtAdjustments,
        dateFrom,
        dateTo,
        t,
      });

      const excelBlob = await debtExport.generateExcel();

      if (excelBlob.size === 0) {
        toast.error(t('common.error'), t('suppliers.debtExport.emptyFileError'));
        return;
      }

      // Download the file
      const url = window.URL.createObjectURL(excelBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${t('suppliers.debtExport.fileName')}_${supplier.code}_${dateFrom}_${dateTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('common.success'), t('suppliers.debtExport.exportSuccess'));
    } catch (error) {
      console.error('Debt export failed:', error);
      toast.error(t('common.error'), t('suppliers.debtExport.exportError'));
    }
  };

  // Define tabs
  const tabs = [
    {
      id: 'debt',
      label: t('suppliers.tabs.debtToPay'),
      content: (
        <SupplierDebtTab
          warehouseReceipts={warehouseReceipts}
          supplier={supplier}
          loading={warehouseReceiptsLoading}
          onDateRangeChange={(from, to) => {
            setDebtDateFrom(from);
            setDebtDateTo(to);
          }}
        />
      ),
    },
    {
      id: 'info',
      label: t('suppliers.tabs.info'),
      content: (
        <SupplierInfoTab
          supplier={supplier}
          loading={loading}
        />
      ),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('suppliers.detail')}
      size="2xl"
      footer={
        <div className="flex items-center justify-between w-full">
          {/* Excel export button on the left (show for warehouse receipt history and debt tabs) */}
          {activeTab === 'debt' && (
            <Button
              variant="outline"
              size="md"
              onClick={handleExcelExport}
              disabled={warehouseReceiptsLoading || warehouseReceipts.length === 0}
              icon={<HiDownload className="h-4 w-4" />}
            >
              {t('warehouseReceipts.quickView.exportExcel')}
            </Button>
          )}

          {/* Close button on the right */}
          <div className={activeTab === 'debt' ? '' : 'ml-auto'}>
            <Button
              variant="primary"
              size="md"
              onClick={onClose}
            >
              {t('buttons.close')}
            </Button>
          </div>
        </div>
      }
    >
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">{t('common.loading')}</div>
        </div>
      )}

      {!loading && !supplier && (
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">{t('suppliers.messages.noSupplierSelected')}</div>
        </div>
      )}

      {!loading && supplier && (
        <TabContainer
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          className="h-full"
        />
      )}

      {/* Debt Export Modal */}
      <SupplierDebtExportModal
        isOpen={showDebtExportModal}
        onClose={() => setShowDebtExportModal(false)}
        supplier={supplier}
        warehouseReceipts={warehouseReceipts}
        onExport={handleDebtExport}
        initialDateFrom={debtDateFrom}
        initialDateTo={debtDateTo}
      />
    </Modal>
  );

};

export default SupplierDetailModal;
